import * as types from '../types';
import { parseArgs } from '../util';

import { LoggerConfiguration } from './LoggerConfiguration';

/**
 * An abstract class that implements the core logging public API.
 */
export abstract class LoggerPublicApi
  extends LoggerConfiguration
  implements types.ILoggerPublicApi
{
  private log(level: types.LogLevel, params: types.ParsedArgs): void {
    for (const strategy of this.strategies) {
      strategy.logWithStrategy(this, level, params);
    }
  }

  private processArgs(
    level: types.LogLevel,
    arg0: unknown,
    arg1?: string | types.LogContext,
    arg2?: types.LogContext,
  ): types.ParsedArgs {
    const parsed = parseArgs(arg0, arg1, arg2);
    return {
      ...parsed,
      message: parsed.message ? this.formatMessage(level, parsed.message) : undefined,
    };
  }

  public debug(message: string, context?: types.LogContext): void;

  public debug(context: types.LogContext): void;

  /**
   * Issues a log that is composed of the optionally provided message, {@link string} and context,
   * {@link types.LogContext}, with the {@link LogLevel.Debug} level.
   */
  public debug(message: string | types.LogContext, ctx?: types.LogContext): void {
    this.log(types.LogLevel.Debug, this.processArgs(types.LogLevel.Debug, message, ctx));
  }

  public error(error: unknown, message?: string, context?: types.LogContext): void;

  public error(errorOrMessage: unknown, context?: types.LogContext): void;

  public error(context: types.LogContext): void;

  /**
   * Issues a log that is composed of the optionally provided message, {@link string}, context,
   * {@link types.LogContext}, and error-object, {@link unknown}, with the {@link LogLevel.Error}
   * level.
   */
  public error(arg0: unknown, arg1?: string | types.LogContext, arg2?: types.LogContext): void {
    this.log(types.LogLevel.Error, this.processArgs(types.LogLevel.Error, arg0, arg1, arg2));
  }

  public info(message: string, context?: types.LogContext): void;

  public info(context: types.LogContext): void;

  /**
   * Issues a log that is composed of the optionally provided message, {@link string} and context,
   * {@link types.LogContext}, with the {@link LogLevel.Info} level.
   */
  public info(messageOrContext: string | types.LogContext, ctx?: types.LogContext): void {
    this.log(types.LogLevel.Info, this.processArgs(types.LogLevel.Info, messageOrContext, ctx));
  }

  public trace(message: string, context?: types.LogContext): void;

  public trace(context: types.LogContext): void;

  /**
   * Issues a log that is composed of the optionally provided message, {@link string} and context,
   * {@link types.LogContext}, with the {@link LogLevel.Trace} level.
   */
  public trace(messageOrContext: string | types.LogContext, ctx?: types.LogContext): void {
    this.log(types.LogLevel.Trace, this.processArgs(types.LogLevel.Trace, messageOrContext, ctx));
  }

  public warn(error: unknown, message: string, context?: types.LogContext): void;

  public warn(errorOrMessage: unknown, context?: types.LogContext): void;

  public warn(context: types.LogContext): void;

  /**
   * Issues a log that is composed of the optionally provided message, {@link string}, context,
   * {@link types.LogContext}, and error-object, {@link Error}, with the {@link LogLevel.Warn}
   * level.
   */
  public warn(arg0: unknown, arg1?: string | types.LogContext, arg2?: types.LogContext): void {
    this.log(types.LogLevel.Warn, this.processArgs(types.LogLevel.Warn, arg0, arg1, arg2));
  }
}
